import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import * as R from 'ramda/src/index';
import moment from 'moment';
import Segment from '../Segment';
import {formatDuration, stopsText} from '../../common';
import {formatMonthDayPeriod} from 'lib/helpers/globalize-helper';
import classNames from 'classnames';
import {formatPrice} from 'lib/helpers/globalize-helper';

const getDepCode = R.pipe(
  R.head,
  R.path(['route', 'departure', 'location'])
);

const getArrCode = R.pipe(
  R.last,
  R.path(['route', 'arrival', 'location'])
);

const getRoute = (segments) => (
  `${getDepCode(segments).city} - ${getArrCode(segments).city}`
);

const getCityFromSearchQuery = (text) => (
  text.split('-')[0]
);

const ModalDetails = ({legs, providers, searchQuery, checkLastSeats, closeResultModal, translations}) => {
  const {totalTripDuration, time, stops,
    bookNow, nextDayArrival, afterMidnightArrival} = translations;
  const {dep, arr, isRoundtrip, obDate, ibDate} = searchQuery;
  const displayLastSeat = checkLastSeats <= 3;
  const seatsLeftMsg = displayLastSeat && (checkLastSeats === 1) ? translations.lastSeat : translations.lastSeats;
  return (
    <Fragment>
      <div className="p-3">
        <button type="button" className="close" onClick={closeResultModal}>
          <span aria-hidden="true">&times;</span>
        </button>
        <h3 className="mb-0 leading-none">{getCityFromSearchQuery(dep)} &gt; {getCityFromSearchQuery(arr)}</h3>
        <div className="leading-small">
          {formatMonthDayPeriod(obDate)}{isRoundtrip && ` - ${formatMonthDayPeriod(ibDate)}`}
        </div>
        {displayLastSeat &&
          <div className="text-danger text-small">{checkLastSeats} {seatsLeftMsg}</div>
        }
      </div>
      <div className="list-group list-group-flush">
        {providers
          .sort((a, b) => a.total - b.total)
          .map(({provider, total, resourceId, id}) => {
            const btnClass = classNames('btn', 'btn-sm', {
              'btn-primary': provider.presentationName !== 'airtickets',
              'btn-secondary': provider.presentationName === 'airtickets'
            });
            return (
              <div className="list-group-item d-flex align-items-center bg-light py-2" key={id}>
                <div>{provider.presentationName}</div>
                <div className="ml-auto">
                  <span className="mr-2 font-bold">{formatPrice(total)}</span>
                  <a
                    href={`partner-redirect?name=${provider.name}&resourceId=${resourceId}&id=${id}`}
                    className={btnClass}
                    target={provider.name !== 'airtickets' ? '_blank' : undefined}
                    rel="noreferrer"
                  >
                    {bookNow}
                  </a>
                </div>
              </div>
            );
          }
          )}
        {
          legs.map((
            {segments, duration, stopsCount, isNextDayArrival, isMidnightArrival},
            index
          ) => (
            <div className="list-group-item" key={index}>
              <div className="display-4 font-normal">{getRoute(segments)}</div>
              <div>
                {segments[0].route.departure.datetime.format('ll')}
                {
                  (isNextDayArrival || isMidnightArrival) &&
                  <span>
                    &nbsp;-&nbsp;
                    <span className="text-danger">
                      {isNextDayArrival && nextDayArrival}
                      {isMidnightArrival && afterMidnightArrival}
                    </span>
                  </span>
                }
              </div>
              <div className="mb-3">
                {totalTripDuration}:&nbsp;
                {formatDuration(moment.duration(duration, 'minutes'), time)},&nbsp;
                {stopsText(stopsCount, stops)}
              </div>
              {
                segments.map((segment, index) => (
                  <Segment
                    key={index}
                    segment={segment}
                    translations={translations}
                  />
                ))
              }
            </div>
          ))
        }
      </div>
    </Fragment>
  );
};

ModalDetails.propTypes = {
  checkLastSeats: PropTypes.number.isRequired,
  legs: PropTypes.array.isRequired,
  providers: PropTypes.array.isRequired,
  searchQuery: PropTypes.object.isRequired,
  closeResultModal: PropTypes.func.isRequired,
  translations: PropTypes.shape({
    totalTripDuration: PropTypes.string.isRequired,
    time: PropTypes.object.isRequired,
    stops: PropTypes.object.isRequired,
    bookNow: PropTypes.string.isRequired,
    nextDayArrival: PropTypes.string.isRequired,
    afterMidnightArrival: PropTypes.string.isRequired,
    lastSeat: PropTypes.string.isRequired,
    lastSeats: PropTypes.string.isRequired
  }).isRequired
};

export default observer(ModalDetails);
